/* eslint-disable no-param-reassign */
import _ from 'lodash';
import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueInfoSnackbar, enqueueErrorSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import { notifyReindexRequired } from '../features/operation-supervisor/indexingRequestEvents';

const initialState = {
  entities: {}, // { [id]: synonym }
  ids: [], // Array of ids preserving order
  savedEntities: {},
  savedIds: [],
  deletedIds: [], // IDs of server-persisted rows deleted locally but not yet saved
  isFetching: false,
  isSaving: false,
  error: null,
  loadError: null,  // only set by load failure; cleared on next load attempt
  searchTerm: '',
  sortBy: 'left',
  sortOrder: 'asc',
  suggestionItems: [],
  suggestionNextCursor: null,
  suggestionsLoading: false,
  suggestionReviewIds: {},
  suggestionError: null,
};

export const normalizeSynonymTerms = (values) => {
  const rawValues = Array.isArray(values) ? values : String(values || '').split(',');
  return rawValues
    .map((value) => String(value).trim())
    .filter((value, index, normalized) => value && normalized.indexOf(value) === index);
};

const normalizeSynonyms = (synonyms) => {
  const entities = {};
  const ids = [];
  (synonyms || []).forEach((synonym) => {
    entities[synonym._id] = synonym;
    ids.push(synonym._id);
  });
  return { entities, ids };
};

const synonymsSlice = createSlice({
  name: 'synonyms',
  initialState,
  reducers: {
    initializeSynonyms: (state, { payload }) => {
      const { entities, ids } = normalizeSynonyms(payload);
      state.entities = entities;
      state.ids = ids;
      state.savedEntities = { ...entities };
      state.savedIds = [...ids];
      state.deletedIds = [];
    },
    addSynonym: (state) => {
      const tempId = `temp-${Date.now()}`;
      state.entities[tempId] = {
        _id: tempId,
        left: [],
        right: [],
        bidirectional: true,
        enabled: true,
        _isNew: true,
      };
      state.ids.unshift(tempId);
    },
    applyImportedSynonyms: (state, { payload }) => {
      const { entities, ids } = normalizeSynonyms(payload || []);
      const importedIds = new Set(ids);
      state.entities = entities;
      state.ids = ids;
      state.deletedIds = state.savedIds.filter(id => !importedIds.has(id));
      state.searchTerm = '';
      state.error = null;
    },
    updateSynonym: (state, { payload }) => {
      const { id, changes } = payload;
      if (state.entities[id]) {
        state.entities[id] = { ...state.entities[id], ...changes };
      }
    },
    deleteSynonym: (state, { payload }) => {
      // Track server-persisted deletions so selectHasChanges stays true after a delete-only edit.
      // _isNew rows are temp-only; removing them needs no tracking.
      const syn = state.entities[payload];
      if (syn && !syn._isNew) {
        state.deletedIds.push(payload);
      }
      state.ids = state.ids.filter((id) => id !== payload);
      delete state.entities[payload];
    },
    discardChanges: (state) => {
      state.entities = { ...state.savedEntities };
      state.ids = [...state.savedIds];
      state.deletedIds = [];
      state.error = null;
    },
    setSearchTerm: (state, { payload }) => {
      state.searchTerm = payload;
    },
    setSort: (state, { payload }) => {
      state.sortBy = payload.sortBy;
      state.sortOrder = payload.sortOrder;
    },
    setFetching: (state, { payload }) => {
      state.isFetching = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
  },
});

export const { actions } = synonymsSlice;

export const selectAllSynonyms = createSelector(
  (state) => state.synonyms.entities,
  (state) => state.synonyms.ids,
  (entities, ids) => ids.map((id) => entities[id]),
);

export const selectFilteredSortedSynonyms = createSelector(
  selectAllSynonyms,
  (state) => state.synonyms.searchTerm,
  (state) => state.synonyms.sortBy,
  (state) => state.synonyms.sortOrder,
  (state) => state.synonyms.savedEntities,
  (synonyms, searchTerm, sortBy, sortOrder, savedEntities) => {
    // Keep live edits on their current page and in their original search match
    // until Save, even when their new text would change the sorting/filtering.
    const sortSource = synonym => (
      (synonym._isModified && savedEntities?.[synonym._id]) || synonym
    );
    const search = searchTerm.toLowerCase();
    const matchesSearch = synonym => (
      synonym.left.join(' ').toLowerCase().includes(search)
      || synonym.right.join(' ').toLowerCase().includes(search)
    );
    const filtered = synonyms.filter(syn => matchesSearch(syn) || matchesSearch(sortSource(syn)));

    const [newRows, persistedRows] = _.partition(filtered, (syn) => syn._isNew);
    return [
      ...newRows,
      ..._.orderBy(persistedRows, [synonym => sortSource(synonym)[sortBy]], [sortOrder.toLowerCase()]),
    ];
  },
);

export const selectHasChanges = createSelector(
  selectAllSynonyms,
  (state) => state.synonyms.deletedIds,
  (synonyms, deletedIds) =>
    deletedIds.length > 0 || synonyms.some((syn) => syn._isNew || syn._isModified),
);

export const selectHasInvalidSynonyms = createSelector(
  selectAllSynonyms,
  (synonyms) => synonyms.some((synonym) => (
    normalizeSynonymTerms(synonym.left).length === 0
    || normalizeSynonymTerms(synonym.right).length === 0
  )),
);

// Thunks — transport-agnostic, API injected via serviceRegistry
export const loadSynonyms = createAsyncThunk(
  'synonyms/loadSynonymsAsync',
  async (shopId, { extra: { serviceRegistry }, dispatch }) => {
    try {
      const { synonyms } = await serviceRegistry.synonymsApi.load(shopId);
      return synonyms;
    } catch (error) {
      const message = error.message || getLocalizedMessage('notifications.error.fetchingError', { entity: 'synonyms' });
      dispatch(enqueueErrorSnackbar(message, { variant: 'error' }));
      throw error;
    }
  },
);

export const saveSynonyms = createAsyncThunk(
  'synonyms/saveSynonymsAsync',
  async (request, {
    extra: { serviceRegistry }, dispatch, getState, rejectWithValue,
  }) => {
    try {
      const shopId = request && typeof request === 'object' ? request.shopId : request;
      const deferredReindex = request && typeof request === 'object'
        ? request.deferredReindex === true
        : false;
      const allSynonyms = request && typeof request === 'object' && Array.isArray(request.synonyms)
        ? request.synonyms
        : selectAllSynonyms(getState());

      const hasInvalidSynonyms = allSynonyms.some((synonym) => (
        normalizeSynonymTerms(synonym.left).length === 0
        || normalizeSynonymTerms(synonym.right).length === 0
      ));
      if (hasInvalidSynonyms) {
        const validationMessage = getLocalizedMessage('search.synonyms.validation.saveBlocked', {});
        dispatch(enqueueErrorSnackbar(validationMessage, { variant: 'error' }));
        return rejectWithValue(validationMessage);
      }

      const payload = allSynonyms.map((syn) => ({
        enabled: syn.enabled,
        _id: syn._isNew ? undefined : syn._id,
        left: normalizeSynonymTerms(syn.left),
        right: normalizeSynonymTerms(syn.right),
        bidirectional: syn.bidirectional,
        source: syn.source || 'manual',
      }));

      const { synonyms: serverSynonyms } = await serviceRegistry.synonymsApi.save(shopId, payload);

      if (deferredReindex) {
        try {
          if (typeof serviceRegistry.indexingApi?.markRequired === 'function') {
            await serviceRegistry.indexingApi.markRequired(shopId);
          } else {
            notifyReindexRequired({ source: 'synonyms-save' });
          }
        } catch (indexingError) {
          dispatch(enqueueErrorSnackbar(
            indexingError?.message || getLocalizedMessage('catalog.update.markRequiredFailed', {}),
            { variant: 'error' },
          ));
        }
      } else if (serviceRegistry.indexingApi && typeof serviceRegistry.indexingApi.startFull === 'function') {
        try {
          await serviceRegistry.indexingApi.startFull(shopId);
          dispatch(enqueueInfoSnackbar(
            getLocalizedMessage('operation.indexing.started', {}),
            { variant: 'info' },
          ));
        } catch (indexingError) {
          dispatch(enqueueErrorSnackbar(
            indexingError?.message || getLocalizedMessage('operation.indexing.startFailed', {}),
            { variant: 'error' },
          ));
        }
      }
      const successMessage = getLocalizedMessage('notifications.success.savedSuccessfully', { entity: 'synonyms' });
      dispatch(enqueueInfoSnackbar(successMessage, { variant: 'success' }));
      return serverSynonyms;
    } catch (error) {
      const errorMessage = error.message || getLocalizedMessage('notifications.error.savingError', { entity: 'synonyms' });
      dispatch(enqueueErrorSnackbar(errorMessage, { variant: 'error' }));
      throw error;
    }
  },
  { condition: (_request, { getState }) => !getState().synonyms.isSaving },
);

export const loadSynonymSuggestions = createAsyncThunk(
  'synonyms/loadSuggestions',
  async ({ shopId, cursor = null, append = false }, {
    extra: { serviceRegistry }, dispatch, rejectWithValue,
  }) => {
    if (!serviceRegistry.synonymSuggestionsApi) return { suggestions: [], next_cursor: null, append };
    try {
      const response = await serviceRegistry.synonymSuggestionsApi.list(shopId, {
        status: 'pending', cursor, limit: 50,
      });
      return { ...response, append };
    } catch (error) {
      const message = error?.message || getLocalizedMessage('search.synonyms.suggestions.loadFailed', {});
      dispatch(enqueueErrorSnackbar(message, { variant: 'error' }));
      return rejectWithValue(message);
    }
  },
);

export const reviewSynonymSuggestion = createAsyncThunk(
  'synonyms/reviewSuggestion',
  async ({ shopId, suggestion, decision, deferredReindex = false }, {
    extra: { serviceRegistry }, dispatch, rejectWithValue,
  }) => {
    if (!serviceRegistry.synonymSuggestionsApi) {
      return rejectWithValue(getLocalizedMessage('search.synonyms.suggestions.unavailable', {}));
    }
    try {
      const response = await serviceRegistry.synonymSuggestionsApi.review(shopId, [{
        suggestion_id: suggestion.id,
        expected_revision: suggestion.revision,
        decision,
      }]);
      if (decision === 'accept') {
        await dispatch(loadSynonyms(shopId));
        if (response.reindex_required && deferredReindex) {
          try {
            if (typeof serviceRegistry.indexingApi?.markRequired === 'function') {
              await serviceRegistry.indexingApi.markRequired(shopId);
            } else {
              notifyReindexRequired({ source: 'synonym-suggestion-accept' });
            }
          } catch (indexingError) {
            dispatch(enqueueErrorSnackbar(
              indexingError?.message || getLocalizedMessage('catalog.update.markRequiredFailed', {}),
              { variant: 'error' },
            ));
          }
        } else if (response.reindex_required && serviceRegistry.indexingApi?.startFull) {
          try {
            await serviceRegistry.indexingApi.startFull(shopId);
            dispatch(enqueueInfoSnackbar(
              getLocalizedMessage('operation.indexing.started', {}),
              { variant: 'info' },
            ));
          } catch (indexingError) {
            dispatch(enqueueErrorSnackbar(
              indexingError?.message || getLocalizedMessage('operation.indexing.startFailed', {}),
              { variant: 'error' },
            ));
          }
        }
      }
      dispatch(enqueueInfoSnackbar(
        getLocalizedMessage(
          decision === 'accept'
            ? 'search.synonyms.suggestions.accepted'
            : 'search.synonyms.suggestions.rejected',
          {},
        ),
        { variant: 'success' },
      ));
      return { id: suggestion.id, decision, response };
    } catch (error) {
      const message = error?.message || getLocalizedMessage('search.synonyms.suggestions.reviewFailed', {});
      dispatch(enqueueErrorSnackbar(message, { variant: 'error' }));
      return rejectWithValue(message);
    }
  },
);

// Extra reducers for thunk lifecycle
const synonymsSliceWithThunks = createSlice({
  name: 'synonyms',
  initialState,
  reducers: synonymsSlice.caseReducers,
  extraReducers: (builder) => {
    builder
      .addCase(loadSynonyms.pending, (state) => {
        state.isFetching = true;
        state.error = null;
        state.loadError = null;
      })
      .addCase(loadSynonyms.fulfilled, (state, { payload }) => {
        const { entities, ids } = normalizeSynonyms(payload);
        state.entities = entities;
        state.ids = ids;
        state.savedEntities = { ...entities };
        state.savedIds = [...ids];
        state.deletedIds = [];
        state.isFetching = false;
        state.loadError = null;
      })
      .addCase(loadSynonyms.rejected, (state, { error }) => {
        state.error = error.message;
        state.loadError = error.message;
        state.isFetching = false;
      })
      .addCase(saveSynonyms.pending, (state) => {
        state.isSaving = true;
        state.error = null;
        // loadError intentionally not cleared on save — load state is unaffected
      })
      .addCase(saveSynonyms.fulfilled, (state, { payload }) => {
        const { entities, ids } = normalizeSynonyms(payload);
        state.entities = entities;
        state.ids = ids;
        state.savedEntities = { ...entities };
        state.savedIds = [...ids];
        state.deletedIds = [];
        state.isFetching = false;
        state.isSaving = false;
      })
      .addCase(saveSynonyms.rejected, (state, { error, payload }) => {
        state.error = payload || error.message;
        // loadError NOT set — save failure must not hide the page surface
        state.isFetching = false;
        state.isSaving = false;
      })
      .addCase(loadSynonymSuggestions.pending, (state) => {
        state.suggestionsLoading = true;
        state.suggestionError = null;
      })
      .addCase(loadSynonymSuggestions.fulfilled, (state, { payload }) => {
        state.suggestionItems = payload.append
          ? [...state.suggestionItems, ...(payload.suggestions || [])]
          : (payload.suggestions || []);
        state.suggestionNextCursor = payload.next_cursor || null;
        state.suggestionsLoading = false;
      })
      .addCase(loadSynonymSuggestions.rejected, (state, { error, payload }) => {
        state.suggestionsLoading = false;
        state.suggestionError = payload || error.message;
      })
      .addCase(reviewSynonymSuggestion.pending, (state, action) => {
        state.suggestionReviewIds[action.meta.arg.suggestion.id] = true;
        state.suggestionError = null;
      })
      .addCase(reviewSynonymSuggestion.fulfilled, (state, { payload }) => {
        delete state.suggestionReviewIds[payload.id];
        state.suggestionItems = state.suggestionItems.filter(item => item.id !== payload.id);
      })
      .addCase(reviewSynonymSuggestion.rejected, (state, action) => {
        delete state.suggestionReviewIds[action.meta.arg.suggestion.id];
        state.suggestionError = action.payload || action.error.message;
      });
  },
});

export default synonymsSliceWithThunks.reducer;
